import { VisitTypeDto } from '@lmt/shared-types';
import {
  RowClickEvent,
  SortOrder,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { TableDotMenu } from '@lmt/unicorn-shared/app/components/common/table/TableDotMenu';
import { secondsToTimeString } from '@lmt/unicorn-shared/app/helpers/date-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useEventCallback } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@lmt/unicorn-shared/src/app/components/common/dataGrid/DataGrid';
import { IconButton } from '../../common/ui-kit/IconButton';
import ArrowDown from '../../../icons/arrow-down.svg?react';
import styles from './VisitTypeList.module.scss';

type Props = {
  data: VisitTypeDto[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  manualOrdering: boolean;
  onReorder: (value: VisitTypeDto, direction: 'up' | 'down') => void;
};

const TableKeys: { [dictionary: string]: string } = {
  value: 'value',
  startTime: 'start_time',
  duration: 'duration',
  option: 'option',
};

export const VisitTypeTable: FC<Props> = (props) => {
  const i18nextDataBlock = useScopedTranslation(
    'dictionaries',
    NS_DataBlockFields,
  );
  const i18nextGlobal = useTranslation();

  const onRowClick = useEventCallback((e: RowClickEvent) => {
    props.onEdit(e.data.id);
  });

  const columns = useMemo(() => {
    const columns = [];
    if (props.manualOrdering) {
      columns.push({
        key: 'manualOrderPosition',
        dataField: 'manualOrderPosition',
        caption: '',
        allowSorting: false,
        defaultSortOrder: 'asc' as SortOrder,
        width: '76px',
        cellRender: (cell: any) => (
          <>
            {cell.rowIndex > 0 ? (
              <IconButton
                color="grey"
                iconComponent={<ArrowDown className={styles.upsideDown} />}
                className={styles.reorderButton}
                onClick={() => props.onReorder(cell.data, 'up')}
                testId={'reorder-up'}
              />
            ) : null}
            {cell.rowIndex != props.data.length - 1 ? (
              <IconButton
                color="grey"
                iconComponent={<ArrowDown />}
                className={styles.reorderButton}
                onClick={() => props.onReorder(cell.data, 'down')}
                testId={'reorder-down'}
              />
            ) : null}
          </>
        ),
      });
    }
    columns.push(
      {
        key: TableKeys.value,
        caption: i18nextDataBlock.t(
          `visit_types.columns.${TableKeys.value}` as any,
        ) as any,
        allowSorting: !props.manualOrdering,
        defaultSortOrder: !props.manualOrdering
          ? ('asc' as SortOrder)
          : undefined,
        cellRender: (cell: any) => i18nextGlobal.t(cell.data.value as any),
      },
      {
        key: TableKeys.startTime,
        caption: i18nextDataBlock.t(
          `visit_types.columns.${TableKeys.startTime}` as any,
        ) as any,
        cellRender: (cell: any) =>
          secondsToTimeString(
            cell.data.specificStartTimeInSecondsSinceMidnight,
          ),
      },
      {
        key: TableKeys.duration,
        caption: i18nextDataBlock.t(
          `visit_types.columns.${TableKeys.duration}` as any,
        ) as any,
        cellRender: (cell: any) => {
          return cell.data.defaultDurationInSeconds / 3600;
        },
      },
      {
        key: TableKeys.option,
        caption: '',
        width: '60px',
        cellRender: (cell: any) => (
          <div
            onClick={(event: any) => event.stopPropagation()}
            style={{ margin: -6 }}
          >
            <TableDotMenu
              options={[
                {
                  title: i18nextDataBlock.t('list.options.delete_button'),
                  onClick: () => props.onDelete(cell.data.id),
                  isDeleteItem: true,
                },
              ]}
            />
          </div>
        ),
      },
    );
    return columns;
  }, [props.manualOrdering]);

  return (
    <DataGrid
      dataSource={props.data}
      onRowClick={onRowClick}
      sorting={{ mode: 'none' }}
      columns={columns}
    />
  );
};
